import React from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Container, Stack, Input, Button, Alert, Progress, Card, CardContent, CardHeader, CardTitle } from '@/components/ui';
import { LogOut } from 'lucide-react';
import { OrgKnowledgeService } from '@/services/OrgKnowledgeService';
import { OrgService } from '@/services/OrgService';
import { getActiveOrgId, useOrgStore } from '@/stores/orgStore';
import { useAuthStore } from '@/stores/authStore';

type Step = 'website' | 'intro' | 'done';

export function Onboarding() {
  const svc = new OrgKnowledgeService();
  const navigate = useNavigate();
  const user = useAuthStore(s => s.user);
  // Removed initialized and fetchOrgs since we no longer fetch orgs here
  const [step, setStep] = React.useState<Step>('website');
  const [websiteUrl, setWebsiteUrl] = React.useState('');
  const [error, setError] = React.useState<string | null>(null);
  const [loading, setLoading] = React.useState(false);
  const orgs = useOrgStore(s => s.orgs);
  const activeOrgId = useOrgStore(s => s.activeOrgId);

  const isValidUrl = (value: string) => {
    const s = value.trim();
    if (!/^https?:\/\//i.test(s)) return false;
    try { new URL(s); return true; } catch { return false; }
  };

  const startResearch = async () => {
    try {
      setLoading(true);
      setError(null);
      if (!isValidUrl(websiteUrl)) {
        setError('Please enter a valid URL starting with http:// or https://');
        return;
      }
      await svc.validateWebsite(websiteUrl.trim());
      await svc.beginProfileResearch(websiteUrl.trim());
      setStep('intro');
    } catch (e: any) {
      setError(e?.message || 'Failed to start research');
    } finally {
      setLoading(false);
    }
  };

  const finish = async () => {
    setStep('done');
    let orgId = getActiveOrgId();
    try {
      if (!orgId) {
        // Get org ID from store since it's already initialized from bootstrap
        orgId = useOrgStore.getState().activeOrgId;
      }
      if (orgId) {
        const orgSvc = new OrgService();
        await orgSvc.markOnboardingComplete(orgId);
        try { localStorage.setItem(`ima_onboarding_completed_${orgId}`, '1'); } catch {}
        // Note: No need to refetch orgs, they're already in the store
      }
    } catch {
      // ignore; navigation still proceeds, loader has local fallback
    }
    navigate('/dashboard', { replace: true });
  };

  React.useEffect(() => {
    // If onboarding already completed, keep user on dashboard
    const active = orgs.find(o => o.id === activeOrgId);
    let completed = Boolean(active?.onboarding_completed);
    if (!completed && active) {
      try { completed = localStorage.getItem(`ima_onboarding_completed_${active.id}`) === '1'; } catch {}
    }
    if (completed) {
      navigate('/dashboard', { replace: true });
    }
  }, [orgs, activeOrgId, navigate]);

  // Note: Organization data is now initialized from bootstrap in the router loader
  // This prevents redundant API calls that caused infinite loops

  return (
    <div className="min-h-screen bg-gradient-to-br from-primary-50 via-white to-secondary-50 flex items-center justify-center p-4">
      <div className="w-full max-w-lg">

        <div className="bg-background rounded-2xl shadow-xl overflow-hidden border">
          <div className="p-6 md:p-8">
            <div className="flex items-center justify-between mb-2">
              <h2 className="text-xl font-semibold">Let’s set up your workspace</h2>
              <Button variant="ghost" size="sm" className="whitespace-nowrap inline-flex items-center gap-2" onClick={async () => { await useAuthStore.getState().logout(); navigate('/', { replace: true }); }}>
                <LogOut className="w-4 h-4" />
                <span>Sign out</span>
              </Button>
            </div>
            <p className="text-sm text-muted-foreground mb-4">We’ll pull key details from your website to personalize content suggestions.</p>

            <Progress value={step === 'website' ? 33 : step === 'intro' ? 66 : 100} />

            {step === 'website' && (
              <div className="mt-6">
                <Stack gap={4}>
                  <Input
                    label="Company website URL"
                    placeholder="https://example.com"
                    value={websiteUrl}
                    onChange={(e) => setWebsiteUrl(e.target.value)}
                    required
                  />
                  {error && <Alert variant="error">{error}</Alert>}
                  <div className="flex justify-end">
                    <Button variant="primary" onClick={startResearch} loading={loading} disabled={loading || !websiteUrl.trim()}>
                      Continue
                    </Button>
                  </div>
                </Stack>
              </div>
            )}

            {step === 'intro' && (
              <div className="mt-6">
                <Stack gap={4}>
                  <div className="text-sm text-muted-foreground">
                    We’ve started researching your website in the background and will populate your organization profile shortly.
                    Next, explore what you can do here:
                  </div>
                  <ul className="list-disc pl-6 text-sm text-foreground">
                    <li>Generate content ideas tailored to your brand.</li>
                    <li>Organize assets and brand colors in one place.</li>
                    <li>Create a content plan and schedule posts.</li>
                  </ul>
                  <div className="flex justify-end gap-2">
                    <Button variant="secondary" onClick={() => setStep('website')}>Back</Button>
                    <Button variant="primary" onClick={finish}>Finish</Button>
                  </div>
                </Stack>
              </div>
            )}

            {step === 'done' && (
              <Alert variant="success" className="mt-6">All done! Redirecting you to your dashboard…</Alert>
            )}
          </div>
        </div>

        <div className="mt-8 text-center text-xs text-muted-foreground">
          Need help? Contact support.
        </div>
      </div>
    </div>
  );
}


